import { Action } from '@arra/core';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import {
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { MouseEvent, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import CloudOutlinedIcon from '../../ui/icons/categories/co2.png';
import WaterDropOutlinedIcon from '../../ui/icons/categories/ressources.png';
import VolunteerActivismOutlinedIcon from '../../ui/icons/categories/social-utility.png';

import BaseBlock from '../../ui/Block';
import Markdown from '../../ui/Markdown';
import RingImpact from '../card/RingImpact';

import EuroIcon from '../../ui/icons/categories/cost.png';
import HourglassTopIcon from '../../ui/icons/categories/duration.png';
import ManageAccountsIcon from '../../ui/icons/categories/human-resources.png';

const Block = styled(BaseBlock.withComponent('a'))`
  height: 100%;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 24px;
  border-radius: 16px;
  background-color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 16px;

  h3 {
    transition: color 0.3s ease;
    text-decoration: none;
  }

  &:hover {
    text-decoration: none;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    transform: translateY(-4px);

    h3 {
      color: var(--actions);
    }
  }
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 600;
  line-height: 1.4;
  text-align: left;
  width: 100%;
  margin: 0;
  color: #060606;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const scopeIcons: { [key: string]: React.ReactElement } = {
  co2: <img src={CloudOutlinedIcon} alt="co2" style={{ width: '32px' }} />,
  utilite: (
    <img
      src={VolunteerActivismOutlinedIcon}
      alt="utilite"
      style={{ width: '32px' }}
    />
  ),
  ressources: (
    <img
      src={WaterDropOutlinedIcon}
      alt="ressources"
      style={{ width: '32px' }}
    />
  ),
};

const scopeColors: { [key: string]: string } = {
  co2: 'var(--VISTABLUE)',
  utilite: 'var(--LITTLECORAL)',
  ressources: '#00BCD4',
};

const SectorContainer = styled.p`
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
  color: grey;
  display: inline-block;
  align-self: flex-start;
  font-family: Lexend Exa, sans-serif;
`;

const ImpactsContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  gap: 8px;
  flex-grow: 0;
  width: 100%;
  margin: 16px 0;
`;

interface ActionProps {
  action: Action;
}

const ChipsContainer = styled.div`
  display: flex;
  flex-grow: 0;
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  align-self: flex-end;
  flex-wrap: wrap;
  gap: 8px;
`;

const ModalProps = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
  > div:first-of-type {
    flex: 2;
  }
  > div:last-of-type {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

interface ChipData {
  icon: React.ReactElement;
  title: string;
  tooltip: string;
}

type ChipLabels = { [key: number]: string };

// Pure data: easy to reuse server-side
export const chipLabels: { [key: string]: ChipLabels } = {
  cost: {
    [-1]: 'économie',
    0: 'aucun',
    1: 'faible',
    2: 'moyen',
    3: 'élevé',
  },
  duration: {
    [-1]: 'économie',
    0: 'immédiate',
    1: 'courte',
    2: 'moyenne',
    3: 'longue',
  },
  humanResources: {
    0: 'aucune',
    1: 'faibles',
    2: 'moyennes',
    3: 'importantes',
  },
};

const chipDatas: { [key: string]: ChipData } = {
  cost: {
    icon: (
      <img
        src={EuroIcon}
        alt="cost"
        style={{ width: '16px', height: '16px' }}
      />
    ),
    title: 'Coût',
    tooltip: 'Niveau de coût estimé',
  },
  duration: {
    icon: (
      <img
        src={HourglassTopIcon}
        alt="duration"
        style={{ width: '16px', height: '16px' }}
      />
    ),
    title: 'Durée',
    tooltip: "Durée d'implémentation estimée",
  },
  humanResources: {
    icon: (
      <img
        src={ManageAccountsIcon}
        alt="human-resources"
        style={{ width: '16px', height: '16px' }}
      />
    ),
    title: 'RH',
    tooltip: 'Ressources humaines nécessaires',
  },
};

const ChipComponent = ({
  value,
  chipKey,
}: {
  value: number;
  chipKey: string;
}) => {
  const chipData = chipDatas[chipKey];
  const labels = chipLabels[chipKey];

  if (!chipData || !labels) return null;

  const label = labels[value];

  if (!label) return null;

  return (
    <Tooltip title={`${chipData.tooltip} : ${label}`} arrow>
      <Chip variant="outlined" label={label} icon={chipData.icon} />
    </Tooltip>
  );
};

const DetailedActionCard = ({ action }: ActionProps) => {
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const posthog = usePostHog();

  const hasDescription =
    !!action.description && action.description.trim().length > 0;

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (hasDescription) {
      posthog?.capture('action viewed', {
        button_name: 'action_card',
        page_name: 'actions',
        action_name: action.title,
        action_sector: action.sector,
      });
      setDescriptionModalOpen(true);
    }
  };

  const handleClose = (
    event: MouseEvent<HTMLButtonElement | HTMLDivElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setDescriptionModalOpen(false);
  };

  return (
    <Block
      onClick={handleClick}
      target="_blank"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'auto',
      }}
    >
      <SectorContainer className="hzr">{action.sector}</SectorContainer>
      <Title className="hxc" style={{ textOverflow: 'clip' }}>
        {action.title}
      </Title>
      <ImpactsContainer>
        {action.impacts.map((impact, index) => {
          const humanizedImpact = humanizeValue(
            impact.unit,
            impact.value,
            impact.scope,
          );

          return (
            <RingImpact
              icon={scopeIcons[impact.scope]}
              color={scopeColors[impact.scope]}
              evolution={impact.percentage}
              label={`${humanizedImpact.value} ${humanizedImpact.unit}`.trim()}
              currentPercent={
                impact.maxScore && impact.currentScore
                  ? (impact.currentScore / impact.maxScore) * 100
                  : undefined
              }
            />
          );
        })}
      </ImpactsContainer>
      <ChipsContainer>
        {Object.entries(chipDatas).map(([chipKey, chipData]) => (
          <ChipComponent
            key={chipKey}
            chipKey={chipKey}
            value={action[chipKey as keyof typeof action] as number}
          />
        ))}
      </ChipsContainer>

      <Dialog open={descriptionModalOpen} onClose={handleClose} maxWidth="xl">
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingRight: 2,
          }}
        >
          <SectorContainer>{action.sector}</SectorContainer>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            paddingTop: 0,
          }}
        >
          <Title style={{ fontSize: '24px' }}>{action.title}</Title>

          <ModalProps>
            {hasDescription && <Markdown>{action.description}</Markdown>}

            <div style={{ flex: 1 }}>
              <ChipsContainer>
                {action.impacts.map((impact, index) => {
                  const humanizedImpact = humanizeValue(
                    impact.unit,
                    impact.value,
                    impact.scope,
                  );

                  return (
                    <RingImpact
                      key={index}
                      icon={scopeIcons[impact.scope]}
                      color={scopeColors[impact.scope]}
                      evolution={impact.percentage}
                      label={`${humanizedImpact.value} ${humanizedImpact.unit}`.trim()}
                      currentPercent={
                        impact.maxScore && impact.currentScore
                          ? (impact.currentScore / impact.maxScore) * 100
                          : undefined
                      }
                    />
                  );
                })}
              </ChipsContainer>

              <ChipsContainer>
                {Object.entries(chipDatas).map(([chipKey, chipData]) => (
                  <ChipComponent
                    key={chipKey}
                    chipKey={chipKey}
                    value={action[chipKey as keyof typeof action] as number}
                  />
                ))}
              </ChipsContainer>
            </div>
          </ModalProps>
        </DialogContent>
      </Dialog>
    </Block>
  );
};

export type CO2Unit = 'gCO2e' | 'kgCO2e' | 'tCO2e' | 'ktCO2e' | 'MtCO2e' | 'gCO₂e' | 'kgCO₂e' | 'tCO₂e' | 'ktCO₂e' | 'MtCO₂e';
export const co2Units = new Set(['gCO2e', 'kgCO2e', 'tCO2e', 'ktCO2e', 'MtCO2e', 'gCO₂e', 'kgCO₂e', 'tCO₂e', 'ktCO₂e', 'MtCO₂e']);
type HumanizedValue = { value: string; unit: string };

export const humanizeValue = (
  unit: string,
  value: number | null,
  scope: string,
): HumanizedValue => {
  if (!value) return { value: '', unit: '' };

  if (scope !== 'co2') {
    return { value: value.toString(), unit };
  }

  const unitToKgMultiplier: Record<CO2Unit, number> = {
    "gCO₂e": 0.001,
    gCO2e: 0.001,
    "kgCO₂e": 1,
    kgCO2e: 1,
    "tCO₂e": 1_000,
    tCO2e: 1_000,
    "ktCO₂e": 1_000_000,
    ktCO2e: 1_000_000,
    "MtCO₂e": 1_000_000_000,
    MtCO2e: 1_000_000_000,
  };

  const valueInKg = value * (unitToKgMultiplier[unit as CO2Unit] || 1);
  const absValueInKg = Math.abs(valueInKg);

  if (absValueInKg < 0.5) {
    return { value: '0', unit: 'kgCO₂e' };
  }

  if (absValueInKg < 99_999) {
    return {
      value: Math.round(valueInKg).toLocaleString('fr-FR'),
      unit: 'kgCO₂e',
    };
  }

  const valueInTons = valueInKg / 1_000;
  return { value: Math.round(valueInTons).toLocaleString('fr-FR'), unit: 'tCO₂e' };
};

export default DetailedActionCard;
